import { XMLParser } from 'fast-xml-parser';
import intersection from 'lodash/intersection';
import uniqBy from 'lodash/uniqBy';

import { formatData } from '@utilities';
import { takeawaysItem, textItem } from '@utilities/powerpoint';

import {
  getColumnFilterDefaultOption,
  getGroupFilterOptions,
  getIdFilterDefaultOption,
  getIdFilterOptions,
  getRowFilterDefaultOption,
} from './helpers';
import { mapChartToExportItems, mapTableToExportItems } from './export_ir';
import {
  isNativeTreeType,
  mapCardGridToExportItem,
  mapTreeToExportItem,
  transformCardReportData,
  transformTreeReportData,
} from './tree_helpers';

const parser = new XMLParser({
  attributeNamePrefix: '',
  attributesGroupName: '$',
  ignoreAttributes: false,
  ignoreDeclaration: true,
  isArray: (_name, _jpath, _isLeafNode, isAttribute) => !isAttribute,
});

// These are visible JSX elements rendered by Visualizations with no native, headless PowerPoint
// producer. Strict entire-report export must omit their tab rather than claim a partial slide set is
// complete. Layout and filter elements are intentionally not included: they do not add report content.
const UNSUPPORTED_STRICT_ELEMENTS = ['ImageItem', 'ListItem', 'ListView', 'VennDiagram'];

const containsElement = (value, elementName) => {
  if (Array.isArray(value)) return value.some((entry) => containsElement(entry, elementName));
  if (!value || typeof value !== 'object') return false;
  return Object.entries(value).some(
    ([key, child]) => key === elementName || containsElement(child, elementName)
  );
};

const hasUnsupportedStrictContent = (tab) =>
  UNSUPPORTED_STRICT_ELEMENTS.some((elementName) => containsElement(tab, elementName));

// Mirror of Visualizations' default filter state, minus the group filter (which the caller varies to
// export every dropdown option — see buildGroupFilterCombos). Column/id/row default the same as the UI.
const resolveDefaultFilters = ({ activeIdFilter, filters, sets }) => {
  const activeColumnFilters = (filters?.columns || []).map(getColumnFilterDefaultOption);
  const resolvedIdFilter =
    activeIdFilter === undefined
      ? getIdFilterDefaultOption({ id: filters?.id, sets })
      : activeIdFilter;
  const filterSet = sets?.find((set) => set?.id === resolvedIdFilter?.value) || sets?.[0];
  const activeRowFilters = (filters?.rows || []).map((field) =>
    getRowFilterDefaultOption({ field, set: filterSet })
  );

  const chartKeys = activeColumnFilters.map((filter) => filter?.option?.value?.chartKey);
  const targets = activeColumnFilters.map((filter) => filter?.option?.value?.target);
  const activeColumnFilter =
    activeColumnFilters.length > 0
      ? {
          value: {
            chartKey:
              activeColumnFilters.length > 1
                ? intersection(chartKeys[0], chartKeys[1])
                : chartKeys[0],
            target:
              activeColumnFilters.length > 1 ? intersection(targets[0], targets[1]) : targets[0],
          },
        }
      : null;

  return { activeColumnFilter, activeIdFilter: resolvedIdFilter, activeRowFilters };
};

const buildIdFilterViews = ({ filters, sets }) => {
  if (!filters?.id?.length) return [{ activeIdFilter: false, key: '', label: '' }];
  return uniqBy(
    getIdFilterOptions(sets).filter((option) => option?.value != null),
    'value'
  ).map((option) => ({
    activeIdFilter: option,
    key: `id:${option.value}`,
    label: option.label ? String(option.label) : String(option.value),
  }));
};

/**
 * A GroupFilter dropdown gates which datasets are visible; on screen only the selected option renders,
 * so a tab with N group options is really N views of the deck. This returns each option-combination as
 * an activeGroupFilters array (the cartesian product across every group filter — usually just one), so
 * the caller can generate items for each and union them. With no group filters it yields a single empty
 * combo (the default view). Options with no value (e.g. metadata-less sets) are dropped.
 *
 * @param  {Object} filters   { groups: string[] }
 * @param  {String} groupType metadata grouping key (e.g. 'table_grouping')
 * @param  {Array}  sets      the tab's data sets
 * @return {Array<Array>} list of activeGroupFilters ([{ group, option:{ label, value } }])
 */
const buildGroupFilterCombos = ({ filters, groupType, sets }) => {
  const groups = (filters?.groups || []).map((group) => ({
    group,
    options: (getGroupFilterOptions({ group, sets, type: groupType })?.options || []).filter(
      (option) => option?.value != null
    ),
  }));

  return groups.reduce(
    (combos, { group, options }) =>
      combos.flatMap((combo) => options.map((option) => [...combo, { group, option }])),
    [[]]
  );
};

const buildGroupFilterComboKey = (activeGroupFilters = []) =>
  activeGroupFilters.map(({ group, option }) => `${group}:${option?.value}`).join('|');

export const buildReportExportViewKey = ({ activeGroupFilters, activeIdFilter, hasIdFilter }) => {
  const groupFilterComboKey = buildGroupFilterComboKey(activeGroupFilters);
  const idFilterKey =
    hasIdFilter && activeIdFilter?.value != null ? `id:${activeIdFilter.value}` : '';

  return [idFilterKey, groupFilterComboKey ? `groups:${groupFilterComboKey}` : '']
    .filter(Boolean)
    .join('|');
};

const buildViewKey = ({ activeGroupFilters, activeIdFilter, hasIdFilter }) =>
  buildReportExportViewKey({ activeGroupFilters, activeIdFilter, hasIdFilter });

const withSelectorTitle = ({ item, selectorLabel, tabName }) => {
  if (!selectorLabel) return item;
  const baseTitle = item.title || tabName;
  const title =
    baseTitle?.trim().toLowerCase() === selectorLabel.trim().toLowerCase()
      ? baseTitle
      : [baseTitle, selectorLabel].filter(Boolean).join(' — ');
  return { ...item, title };
};

// Headless dataset filtering for every exported group-filter combination (id -> groups -> rows).
const applyFilters = ({ activeGroupFilters, activeIdFilter, activeRowFilters, groupType, sets }) =>
  (sets || [])
    .filter((set) => (!activeIdFilter ? set : set?.id === activeIdFilter?.value))
    .filter((set) => {
      if (activeGroupFilters?.length < 1) return true;
      return activeGroupFilters.every(
        (filter) => set?.metadata?.[groupType]?.[filter?.group] === filter?.option?.value
      );
    })
    .map((set) => {
      if (activeRowFilters?.length < 1) return set;
      return {
        ...set,
        data: set?.data?.filter((row) => {
          let isValid = true;
          activeRowFilters.forEach((filter) => {
            if (!isValid) return;
            isValid = row[filter?.field] === filter?.option?.value;
          });
          return isValid;
        }),
      };
    });

const mapDetailsToExportItem = (tab) => {
  const notes = (tab.Footnote?.[0]?.FootnoteItem || [])
    .map((item) => item?.$?.text)
    .filter(Boolean);
  const metadata = (tab.Metadata || []).flatMap((section) =>
    (section.MetadataItem || []).map((item) => ({
      label: item?.$?.name,
      value: formatData({ format: item?.$?.format, value: Number(item?.$?.value) }),
    }))
  );
  if (!notes.length && !metadata.length) return null;

  const blocks = [];
  if (notes.length) blocks.push(`Notes\n${notes.map((note) => `• ${note}`).join('\n')}`);
  if (metadata.length) {
    blocks.push(
      `Sample Sizes\n${metadata.map(({ label, value }) => `${label}: ${value}`).join('\n')}`
    );
  }
  return textItem({ body: blocks.join('\n\n'), order: 100, title: 'Notes and Sample Sizes' });
};

const mapAnnouncementToExportItems = (tab) =>
  (tab.Announcement || [])
    .map((announcement, index) => {
      const { description, header, priority } = announcement?.$ || {};
      const body = description || header;
      if (!body) return null;

      if (priority === 'takeaway_summary') {
        return takeawaysItem({ body, order: -100 + index, title: header || 'Key Takeaways' });
      }

      return textItem({ body, order: -100 + index, title: header || 'Report announcement' });
    })
    .filter(Boolean);

const mapSummaryToExportItem = (summary) => {
  if (!summary?.text) return null;
  return takeawaysItem({ body: summary.text, order: -200, title: 'Key Takeaways' });
};

/**
 * Headlessly builds a report tab's PPTX export items — no React render, no DOM. Mirrors the
 * Visualizations data path (default filters -> filteredData -> per-element producers) so the
 * entire-report export can generate purely from the tab API. Image-only visualizations and tree
 * renderer types without native mappings have no headless producer and are dropped.
 *
 * @param  {Array}  askWhyPeopleGroups
 * @param  {Array}  peopleGroupTypes
 * @param  {Object} tabData  transformed tab response ({ filters, sets, groupType, jsx })
 * @param  {String|Number} tabId
 * @param  {String} tabName
 * @return {Array} export items[] (native only; image markers removed)
 */
export const generateTabExportItems = ({
  askWhyPeopleGroups = [],
  peopleGroupTypes = [],
  strict = false,
  tabData,
  tabId,
  tabName,
}) => {
  const { filters = {}, groupType, jsx, sets = [], summary } = tabData || {};
  if (!jsx || !sets.length) return [];
  const idFilterViews = buildIdFilterViews({ filters, sets });
  const combos = buildGroupFilterCombos({ filters, groupType, sets });
  if (!idFilterViews.length || !combos.length) return [];

  const tab = parser.parse(jsx)?.Tab?.[0] || {};
  if (strict && hasUnsupportedStrictContent(tab)) return [];

  // Each group-filter combo is one dropdown view. Dynamic items need the combo in their key so every
  // view is retained; tab-level details are intentionally shared and retain their original key.
  // ORDER_STRIDE keeps each view's charts/tables/cards contiguous and ordered in the deck.
  const ORDER_STRIDE = 1000;
  const byKey = new Map();
  let isComplete = true;
  const add = (item, viewIndex, viewKey) => {
    if (!item?.key) return;
    const key = viewKey ? `${item.key}|${viewKey}` : item.key;
    if (byKey.has(key)) return;
    byKey.set(key, {
      ...item,
      key,
      order: (item.order ?? 0) + viewIndex * ORDER_STRIDE,
      ...(item.type === 'table' ? { sourceViewKey: viewKey || '' } : {}),
    });
  };

  idFilterViews.forEach(({ activeIdFilter, label: selectorLabel }, idIndex) => {
    const initialItemCount = byKey.size;
    const { activeColumnFilter, activeRowFilters } = resolveDefaultFilters({
      activeIdFilter,
      filters,
      sets,
    });
    combos.forEach((activeGroupFilters, comboIndex) => {
      const viewKey = buildViewKey({
        activeGroupFilters,
        activeIdFilter,
        hasIdFilter: Boolean(filters.id?.length),
      });
      const viewIndex = idIndex * combos.length + comboIndex;
      const filteredData = applyFilters({
        activeGroupFilters,
        activeIdFilter,
        activeRowFilters,
        groupType,
        sets,
      });
      if (!filteredData.length) return;

      (tab.Chart || []).forEach((chart) => {
        const attrs = chart?.$ || {};
        const isRendered =
          attrs.defaultDataId === undefined ||
          filteredData.some((set) => set?.id === attrs.defaultDataId);
        if (!isRendered) return;

        const chartItems = mapChartToExportItems({
          activeColumnFilter,
          columnFilters: filters.columns,
          defaultDataId: attrs.defaultDataId,
          filteredData,
          groupBy: attrs.groupBy,
          isSum: attrs.isSum,
          limit: attrs.limit,
          orientation: attrs.orientation,
          tabId,
          type: attrs.type,
          xaxis: attrs.xaxis,
          xAxisFormat: attrs.xAxisFormat,
          xAxisTickAngle: attrs.xAxisTickAngle,
          xAxisTitle: attrs.xAxisTitle,
          xMax: attrs.xMax,
          xMin: attrs.xMin,
          yaxis: attrs.yaxis,
          yAxisFormat: attrs.yAxisFormat,
          yAxisTitle: attrs.yAxisTitle,
          yMax: attrs.yMax,
          yMin: attrs.yMin,
        });
        if (strict && (!chartItems.length || chartItems.some((item) => item.type === 'image'))) {
          isComplete = false;
        }
        chartItems.forEach((item) =>
          add(withSelectorTitle({ item, selectorLabel, tabName }), viewIndex, viewKey)
        );
      });

      (tab.Table || []).forEach((table) => {
        const defaultDataId = table?.$?.defaultDataId;
        const isRendered =
          defaultDataId === undefined || filteredData.some((set) => set?.id === defaultDataId);
        if (!isRendered) return;

        const children = (table?.Column || []).map((column) => ({ props: column?.$ || {} }));
        const tableItems = mapTableToExportItems({
          activeColumnFilter,
          children,
          columnFilters: filters.columns,
          defaultDataId,
          filteredData,
          tabId,
        });
        if (strict && !tableItems.length) isComplete = false;
        tableItems.forEach((item) =>
          add(withSelectorTitle({ item, selectorLabel, tabName }), viewIndex, viewKey)
        );
      });

      if (tab.Tree) {
        const treeType = tab.Tree?.[0]?.$?.type;
        const treeDataSets = filters.id?.length
          ? [filteredData]
          : treeType === 'ebsova-tree'
            ? sets.map((set) => [set])
            : [filteredData];
        treeDataSets.forEach((reportData, treeIndex) => {
          const treeTitle = filters.id?.length
            ? treeType === 'ebsova-tree'
              ? selectorLabel
              : withSelectorTitle({ item: { title: tabName }, selectorLabel, tabName }).title
            : treeType === 'ebsova-tree'
              ? reportData?.[0]?.name
              : tabName;
          const { cardBlocks } = transformTreeReportData({
            askWhyPeopleGroups,
            jsx,
            peopleGroupTypes,
            reportData,
          });
          if (isNativeTreeType(cardBlocks?.type)) {
            add(
              {
                ...mapTreeToExportItem({
                  title: treeTitle,
                  treeNodes: cardBlocks.treeNodes,
                  type: cardBlocks.type,
                }),
                key: `${tabId}|tree|${reportData?.[0]?.id || 'default'}`,
                order: treeIndex * 10,
                tabId,
              },
              viewIndex,
              viewKey
            );
          } else if (strict) isComplete = false;
        });
      }

      if (tab.CardsLayout) {
        const { cardBlocks } = transformCardReportData({
          askWhyPeopleGroups,
          jsx,
          peopleGroupTypes,
          reportData: filteredData,
        });
        const cardItem = mapCardGridToExportItem({ cardBlocks, title: tabName });
        if (strict && !cardItem.cards?.length) isComplete = false;
        add(
          {
            ...withSelectorTitle({ item: cardItem, selectorLabel, tabName }),
            key: `${tabId}|cardslayout`,
            order: 0,
            tabId,
          },
          viewIndex,
          viewKey
        );
      }
    });
    if (strict && byKey.size === initialItemCount) isComplete = false;
  });

  const details = mapDetailsToExportItem(tab);
  const summaryItem = mapSummaryToExportItem(summary);
  if (summaryItem) {
    add(
      {
        ...summaryItem,
        key: `${tabId}|summary`,
        tabId,
      },
      0
    );
  }
  const announcements = mapAnnouncementToExportItems(tab);
  announcements.forEach((announcement, index) => {
    add(
      {
        ...announcement,
        key: `${tabId}|announcement|${index}`,
        tabId,
      },
      0
    );
  });
  if (details) {
    add(
      {
        ...details,
        key: `${tabId}|details`,
        order: (idFilterViews.length * combos.length - 1) * ORDER_STRIDE + 100,
        tabId,
      },
      0
    );
  }

  const items = [...byKey.values()];

  // Image-only markers can't be produced headlessly (no live chart to rasterize) — drop them.
  const renderable = items.filter((item) => item.type !== 'image');
  if (renderable.length < items.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[export] "${tabName}": skipped ${items.length - renderable.length} image-only visualization(s)`
    );
  }
  return strict && !isComplete ? [] : renderable;
};
